// @ts-nocheck
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ownerApi } from './ownerApi';
import { replyHtml } from './replyFormat';
import PageHeader from './ui/PageHeader';
import Button from './ui/Button';
import TechDetails from './ui/TechDetails';
import EmptyState from './ui/EmptyState';
import { SparklesIcon, UserIcon } from '@heroicons/react/24/outline';
import AiCallLines from './AiCallLines';

/** @typedef {{ role: 'user' | 'assistant' | 'status', text: string, calls?: any[] }} BrainLine */

export default function OwnerBrains() {
  const [params, setParams] = useSearchParams();
  const [subscribers, setSubscribers] = useState(/** @type {any[]} */ ([]));
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [draftMode, setDraftMode] = useState(false);
  const [error, setError] = useState('');
  const [customer, setCustomer] = useState(/** @type {BrainLine[]} */ ([]));
  const [copilot, setCopilot] = useState(/** @type {BrainLine[]} */ ([]));
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState('');
  const urlTenant = params.get('tenant') || '';
  const firstLoaded = !loadingTenants ? (subscribers[0]?.tenant_id || '') : '';
  const tenantId = urlTenant || firstLoaded;
  const ai = params.get('ai') === 'copilot' ? 'copilot' : 'customer';
  const tenantRef = useRef(tenantId);
  tenantRef.current = tenantId;

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 8000);
    let live = true;
    ownerApi.brainBusinesses(controller.signal)
      .then((data) => {
        if (!live) return;
        const rows = data.businesses || [];
        setSubscribers(rows);
        if (!params.get('tenant') && rows[0]?.tenant_id) setParams({ tenant: rows[0].tenant_id, ai });
      })
      .catch((reason) => {
        if (!live) return;
        const aborted = reason?.name === 'AbortError' || controller.signal.aborted;
        const message = reason?.message || 'The business list failed.';
        setError(aborted ? 'The business list took too long. Refresh the page.' : message);
      })
      .finally(() => {
        window.clearTimeout(timer);
        if (live) setLoadingTenants(false);
      });
    return () => {
      live = false;
      controller.abort();
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseTenant = (next) => {
    setParams({ tenant: next, ai });
    setCustomer([]);
    setCopilot([]);
    setDraft('');
    setError('');
    setBusy('');
  };

  const thread = ai === 'customer' ? customer : copilot;
  const setThread = ai === 'customer' ? setCustomer : setCopilot;

  const callsFor = (data) => (
    Array.isArray(data.ai_calls) && data.ai_calls.length
      ? data.ai_calls
      : [{ provider: data.provider, model: data.model, cost_usd: data.cost_usd }]
  );

  const send = async () => {
    const message = draft.trim();
    if (loadingTenants || !tenantId || !message || busy) return;
    const startedTenant = tenantId;
    const brain = ai;
    setBusy(brain);
    setDraft('');
    const history = thread.filter((line) => line.role === 'user' || line.role === 'assistant').map((line) => ({ role: line.role, text: line.text }));
    const prior = thread;
    setThread([...prior, { role: 'user', text: message }]);
    const finish = (extra) => {
      if (tenantRef.current !== startedTenant) return;
      setThread([...prior, { role: 'user', text: message }, ...extra]);
    };
    try {
      if (brain === 'customer' && !draftMode) {
        await sendLiveCustomer({ startedTenant, message, history, finish });
        return;
      }
      const data = await ownerApi.brainTurn(brain, {
        tenant_id: startedTenant,
        message,
        history,
        mode: brain === 'customer' && draftMode ? 'draft' : 'live',
      });
      finish(linesFrom(data));
    } catch (reason) {
      finish([{ role: 'status', text: reason instanceof Error ? reason.message : 'Request failed' }]);
    } finally {
      if (tenantRef.current === startedTenant) setBusy('');
    }
  };

  const sendLiveCustomer = async ({ startedTenant, message, history, finish }) => {
    /** @type {BrainLine[]} */
    const shown = [];
    let lunaReply = '';
    try {
      const luna = await ownerApi.brainTurn('customer', {
        tenant_id: startedTenant,
        message,
        history,
        mode: 'live',
        phase: 'luna',
      });
      if (tenantRef.current !== startedTenant) return;
      lunaReply = String(luna.reply || '');
      if (lunaReply) {
        shown.push({ role: 'assistant', text: lunaReply, calls: callsFor(luna) });
        finish(shown);
      }
      if (luna.needs_tira === false && lunaReply) return;
    } catch (reason) {
      if (!lunaReply) {
        finish([{ role: 'status', text: reason instanceof Error ? reason.message : 'Request failed' }]);
        return;
      }
    }
    const tiraHistory = lunaReply ? [...history, { role: 'assistant', text: lunaReply }] : history;
    try {
      const data = await ownerApi.brainTurn('customer', {
        tenant_id: startedTenant,
        message,
        history: tiraHistory,
        mode: 'live',
        phase: 'tira',
      });
      if (tenantRef.current !== startedTenant) return;
      finish([...shown, ...linesFrom(data)]);
    } catch (reason) {
      finish([...shown, { role: 'status', text: reason instanceof Error ? reason.message : 'Request failed' }]);
    }
  };

  const linesFrom = (data) => {
    /** @type {BrainLine[]} */
    const extra = [];
    if (data.reply) extra.push({ role: 'assistant', text: data.reply, calls: callsFor(data) });
    if (data.hint) extra.push({ role: 'status', text: data.hint });
    else if (!data.reply && data.reason) extra.push({ role: 'status', text: 'The AI stopped before a reply.' });
    if (data.pending_confirmation) extra.push({ role: 'status', text: `Waiting for approval: ${data.pending_confirmation}` });
    if (!extra.length) extra.push({ role: 'status', text: 'No reply yet.' });
    return extra;
  };

  const title = ai === 'customer' ? 'Customer AI' : 'Owner Copilot';
  const known = subscribers.find((row) => row.tenant_id === tenantId);
  const pinned = tenantId ? [{ tenant_id: tenantId, business_name: known?.business_name || tenantId }] : [];
  const rest = (loadingTenants ? [] : subscribers).filter((row) => row.tenant_id && row.tenant_id !== tenantId);
  let businesses = [...pinned, ...rest];
  if (loadingTenants && !tenantId) businesses = [{ tenant_id: '', business_name: 'Loading businesses…' }];
  if (!businesses.length) businesses = [{ tenant_id: '', business_name: 'No businesses loaded' }];
  const sendDisabled = loadingTenants || !tenantId || !!busy || !draft.trim();
  return (
    <div className="max-w-[880px] space-y-4">
      <PageHeader title="Test the AI" subtitle="Chat with a business's AI as a customer or as the owner. Tests don't use their messages." />
      <label className="block text-sm text-slate-700">Business
        <select aria-label="Business" value={tenantId} onChange={(event) => chooseTenant(event.target.value)} className="mt-1 h-11 w-full rounded-lg border border-[#7C8798] bg-white px-3">
          {businesses.map((row) => <option key={row.tenant_id || 'loading'} value={row.tenant_id}>{row.business_name || row.email || row.tenant_id}</option>)}
        </select>
        {loadingTenants && tenantId ? <span className="mt-1 block text-sm text-slate-600">Loading businesses…</span> : null}
      </label>
      <div role="tablist" className="flex gap-2">
        <button type="button" role="tab" aria-selected={ai === 'customer'} className={ai === 'customer' ? 'border-b-2 border-[#0F766E] bg-[#F0FDFA] px-3 py-2 text-sm text-[#0F766E]' : 'px-3 py-2 text-sm text-slate-700'} onClick={() => setParams({ tenant: tenantId, ai: 'customer' })}>Customer AI</button>
        <button type="button" role="tab" aria-selected={ai === 'copilot'} className={ai === 'copilot' ? 'border-b-2 border-[#0F766E] bg-[#F0FDFA] px-3 py-2 text-sm text-[#0F766E]' : 'px-3 py-2 text-sm text-slate-700'} onClick={() => setParams({ tenant: tenantId, ai: 'copilot' })}>Owner Copilot</button>
      </div>
      {ai === 'customer' ? (
        <>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={draftMode} onChange={(event) => setDraftMode(event.target.checked)} /> Use unpublished changes</label>
          <p className="text-[13px] text-slate-600">Try your latest edits before customers see them.</p>
        </>
      ) : null}
      {error ? <p role="alert" className="text-sm text-red-800">{error}</p> : null}
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        {thread.length === 0 ? (
          <EmptyState
            icon={ai === 'customer' ? <UserIcon className="h-6 w-6" /> : <SparklesIcon className="h-6 w-6" />}
            title={ai === 'customer' ? 'Write like a customer would' : 'Ask like the business owner would'}
            text={ai === 'customer' ? 'For example: What are your prices?' : 'For example: How many messages did I get this week?'}
          />
        ) : thread.map((line, index) => (
          <p key={`${line.role}-${index}`} className={line.role === 'user' ? 'ml-8 mt-2 rounded-lg bg-teal-50 p-3 text-sm' : 'mr-8 mt-2 rounded-lg border p-3 text-sm'}>
            {line.role === 'assistant' ? <span dangerouslySetInnerHTML={replyHtml(line.text)} /> : line.text}
            {line.role === 'assistant' ? <AiCallLines source={{ ai_calls: line.calls }} /> : null}
            {line.role === 'status' ? <TechDetails text={line.text} /> : null}
          </p>
        ))}
        <form className="mt-4 flex gap-2" onSubmit={(event) => { event.preventDefault(); void send(); }}>
          <textarea aria-label={`Message ${title}`} value={draft} onChange={(event) => setDraft(event.target.value)} className="min-h-11 flex-1 rounded-lg border border-[#7C8798] px-3 py-2 text-sm" placeholder="Type a message…" />
          <Button type="submit" aria-label={`Send to ${title}`} quietDisabled className={sendDisabled ? 'bg-slate-200 text-slate-600' : ''} disabled={sendDisabled}>{busy ? 'Sending…' : 'Send'}</Button>
        </form>
        {loadingTenants ? null : !tenantId ? <p className="mt-2 text-sm text-slate-600">Choose a business first</p> : !draft.trim() ? <p className="mt-2 text-sm text-slate-600">Type a message to send</p> : null}
      </section>
    </div>
  );
}
